/**
 * Arena boot loading screen — staged progress over everything the client pulls before it can
 * play smoothly: the physics engine, the server handshake, weapon and character models, and
 * the map build.
 *
 * Why this exists (the matchmaking flow's client half): ten clients arriving from an accepted
 * match all hit the asset host and build their worlds at once. The room's warmup phase already
 * synchronises the *start*; this screen makes the warmup arrival smooth — models are in cache
 * before the first remote player needs one, instead of every GLB streaming in over the first
 * seconds of play as a hitch-fest.
 *
 * Split in two on purpose:
 *  - `BootProgress` is a pure state machine (stages, weights, fractions) with no DOM — that is
 *    the part the tests pin.
 *  - `mountBootScreen` renders it and removes itself when the machine reports done.
 *
 * The shell warms the same assets one page earlier and hands the bar over — see
 * `recordBootFloor` below and `shell/arena-handoff.ts`.
 *
 * A prefetch FAILURE never blocks joining. Every asset here is also loaded on demand by the
 *  renderer's own memoized loaders (see `character.ts`, `viewmodel.ts`, `held-weapon.ts`), so
 * a missing file degrades to exactly today's behaviour: warn and stream it in later.
 */

import { t } from '../i18n';

export interface BootStage {
  id: string;
  /**
   * The English label, and the fallback.
   *
   * `labelKey` is what actually renders when it is set. Kept as a plain string as well
   * because this module is loaded by `/arena.html`, a separate Vite entry that need not have
   * called `initI18n()` — and a loading screen is the one surface that must read even when
   * nothing else has finished booting.
   */
  label: string;
  /** Catalogue key for `label`, resolved at render (never at module scope — a `t()` in a
   * module-level const freezes the language the tab booted in). */
  labelKey?: string;
  /** Relative share of the bar — rough download/build cost, not a promise. */
  weight: number;
}

/** Stage order is display order and completion order — `current()` reports the first
 * incomplete stage, so out-of-order completion still renders sensibly. */
export const BOOT_STAGES: readonly BootStage[] = [
  { id: 'engine', label: 'Starting engine', labelKey: 'play.loading.engine', weight: 10 },
  { id: 'connect', label: 'Connecting to server', labelKey: 'play.loading.connect', weight: 10 },
  { id: 'weapons', label: 'Loading weapons', labelKey: 'play.loading.weapons', weight: 25 },
  { id: 'characters', label: 'Loading characters', labelKey: 'play.loading.characters', weight: 40 },
  { id: 'map', label: 'Building map', labelKey: 'play.loading.map', weight: 15 },
];

/**
 * Where the shell leaves the bar when it navigates to `/arena.html`.
 *
 * Entering a match is a real page navigation: the shell's loading screen is torn down with its
 * document and this file's screen is built fresh on the next one. Both draw the same bar, so
 * without this the player watches it fill to a third and then snap back to zero — the one
 * visible seam in a hand-off whose entire purpose is to have none.
 *
 * `sessionStorage`, not a query parameter: the arena URL is signed-ticket territory and a
 * cosmetic hint has no business in it. Read once and cleared, so a manual reload of
 * `/arena.html` starts honestly at zero rather than inheriting a stale number.
 */
const BOOT_FLOOR_KEY = 'bl.boot.floor';

/** The hand-off can never claim more than this. The shell only warms assets — it has not
 * connected, and it has not built the map — so a floor high enough to look nearly-done would
 * be a bar that sits full while the real work happens. */
export const BOOT_FLOOR_CEILING = 45;

export function recordBootFloor(percent: number): void {
  try {
    const clamped = Math.min(BOOT_FLOOR_CEILING, Math.max(0, percent));
    sessionStorage.setItem(BOOT_FLOOR_KEY, String(Math.floor(clamped)));
  } catch {
    // Private-mode / disabled storage. The bar restarting at zero is the old behaviour.
  }
}

/** Reads and consumes the floor. Anything unparseable reads as 0 — a garbage value must not
 * be able to pin the bar. */
export function takeBootFloor(): number {
  try {
    const raw = sessionStorage.getItem(BOOT_FLOOR_KEY);
    sessionStorage.removeItem(BOOT_FLOOR_KEY);
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return 0;
    return Math.min(BOOT_FLOOR_CEILING, value);
  } catch {
    return 0;
  }
}

export class BootProgress {
  private readonly fractions = new Map<string, number>();
  private readonly listeners = new Set<() => void>();

  /**
   * @param floorPercent Where the bar starts, 0..`BOOT_FLOOR_CEILING`. Defaults to whatever
   * the shell recorded on its way here, which is why the arena's own screen needs no argument
   * and picks the bar up mid-fill. The shell passes 0 for its own machine — it is the writer,
   * not a reader.
   */
  constructor(
    readonly stages: readonly BootStage[] = BOOT_STAGES,
    private readonly floorPercent: number = takeBootFloor(),
  ) {
    if (stages.length === 0) throw new Error('BootProgress needs at least one stage');
    for (const stage of stages) this.fractions.set(stage.id, 0);
  }

  /** Sets a stage's fraction (clamped 0..1). Progress never goes backwards — a late straggler
   * report from a parallel fetch must not make the bar jump left. Unknown ids throw: a typo'd
   * stage silently absorbing progress is the untestable kind of bug. */
  set(id: string, fraction: number): void {
    const current = this.fractions.get(id);
    if (current === undefined) throw new Error(`unknown boot stage "${id}"`);
    const next = Math.min(1, Math.max(0, fraction));
    if (next <= current) return;
    this.fractions.set(id, next);
    for (const listener of this.listeners) listener();
  }

  complete(id: string): void {
    this.set(id, 1);
  }

  /** Weighted overall progress, 0..100, never below the hand-off floor — the bar only ever
   * moves right, across a page navigation as much as within one. */
  overall(): number {
    let total = 0;
    let done = 0;
    for (const stage of this.stages) {
      total += stage.weight;
      done += stage.weight * (this.fractions.get(stage.id) ?? 0);
    }
    return Math.max(this.floorPercent, (done / total) * 100);
  }

  /** The first incomplete stage — what the label under the bar names. Null when done. */
  current(): BootStage | null {
    for (const stage of this.stages) {
      if ((this.fractions.get(stage.id) ?? 0) < 1) return stage;
    }
    return null;
  }

  get done(): boolean {
    return this.current() === null;
  }

  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }
}

/**
 * Pulls `urls` into the browser's HTTP cache, reporting completed-file fraction as it goes.
 *
 * The body is fully read (`arrayBuffer`) because a response only lands in cache once it is
 * consumed. The renderer's own loaders fetch the same URLs later and hit the cache (or a 304
 * revalidation under Vite's dev headers — still no second body transfer). Failures count as
 * completed: the bar must reach the end even when an asset is missing, because the join must
 * not block on cosmetics.
 */
export async function prefetchIntoCache(
  urls: readonly string[],
  onProgress: (fraction: number) => void,
): Promise<void> {
  if (urls.length === 0) {
    onProgress(1);
    return;
  }
  let finished = 0;
  await Promise.all(
    urls.map(async (url) => {
      try {
        const response = await fetch(url);
        if (response.ok) await response.arrayBuffer();
        else console.warn(`[boot] prefetch ${url}: HTTP ${response.status}`);
      } catch (error) {
        console.warn(`[boot] prefetch ${url} failed`, error);
      } finally {
        finished += 1;
        onProgress(finished / urls.length);
      }
    }),
  );
}

export interface BootScreenHandle {
  /** Removes the screen immediately — the error paths call this so a failed connect is not
   * hidden behind a progress bar that will never finish. Idempotent; the done-path fade calls
   * it too. */
  dispose(): void;
}

/**
 * Renders `progress` as a full-screen overlay and removes itself when the machine reports
 * done (fading out under `motion-safe`, instantly under reduced motion).
 *
 * z-40: above the status overlay pill (z-20) and the HUD, below nothing — while the game is
 * loading there is nothing more important to see. `pointer-events-auto` deliberately: clicks
 * during load must not reach the canvas and race the pointer-lock prompt.
 */
/** Background key art, cover-fit behind the loading bar. Absent gracefully — see its
 * `onerror` below — so a missing file (or a slow CDN) never leaves a broken-image glyph
 * sitting over the loading screen; the plain dark background it started as still reads. */
const LOADING_KEYART_URL = '/game/ui/loading-keyart.jpg';
/** Wordmark shown above the bar in place of the plain text brand. Same fallback rule. */
const LOADING_LOGO_URL = '/game/ui/logo-blackline.png';

/**
 * A stage's label in the player's language, falling back to its English string and then to
 * "Ready" for the done state. Resolved per render rather than once, so the arena's screen is
 * localised without the stage tables becoming language-frozen constants.
 */
function stageLabel(stage: BootStage | null | undefined): string {
  // `t()` renders a missing key as the key itself. A loading screen is the last place that can
  // afford to show `play.loading.weapons` to a player, so every lookup here falls back to the
  // English string rather than trusting the catalogue to be complete.
  const translate = (key: string, fallback: string): string => {
    const value = t(key);
    return value === key ? fallback : value;
  };
  if (!stage) return translate('play.loading.ready', 'Ready');
  return stage.labelKey ? translate(stage.labelKey, stage.label) : stage.label;
}

export function mountBootScreen(container: HTMLElement, progress: BootProgress): BootScreenHandle {
  const screen = document.createElement('div');
  screen.dataset.arena = 'boot-screen';
  screen.className =
    'fixed inset-0 z-40 flex flex-col items-center justify-center gap-6 overflow-hidden bg-[#0a0b0d] ' +
    'motion-safe:transition-opacity motion-safe:duration-300';

  // Key art, darkened 45% and faded to black toward the bottom so the progress bar and
  // label — sitting low in the frame, over whatever the art put there — stay legible
  // regardless of what is behind them.
  const keyart = document.createElement('img');
  keyart.alt = '';
  keyart.setAttribute('aria-hidden', 'true');
  keyart.className = 'absolute inset-0 h-full w-full object-cover';
  keyart.addEventListener('error', () => keyart.remove());
  keyart.src = LOADING_KEYART_URL;

  const darken = document.createElement('div');
  darken.className = 'absolute inset-0 bg-black/45';

  const bottomFade = document.createElement('div');
  bottomFade.className = 'absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-black/90 to-transparent';

  screen.append(keyart, darken, bottomFade);

  // Text brand, kept in the DOM but hidden — the logo image is the primary presentation and
  // this is only what shows if that image fails to load (see its own `onerror` below).
  const brand = document.createElement('div');
  brand.className = 'relative hidden font-heading text-3xl uppercase tracking-[0.12em] text-gold select-none';
  brand.innerHTML = 'BLACK<span class="text-text">LINE</span>';

  const logo = document.createElement('img');
  logo.alt = 'Black Line';
  logo.className = 'relative w-[min(420px,70vw)] h-auto select-none';
  logo.addEventListener('error', () => {
    logo.remove();
    brand.classList.remove('hidden');
  });
  logo.src = LOADING_LOGO_URL;

  // The progress card: a kit window holding the stage name on a header plate, the painted
  // bar, and the percentage in game lettering.
  const card = document.createElement('div');
  card.className = 'kit-window kit-pop relative flex w-[min(460px,86vw)] flex-col gap-3 [--kw-pad:18px_20px]';

  const header = document.createElement('div');
  header.className = 'kit-header justify-center [--kh:46px]';
  const label = document.createElement('div');
  label.dataset.arena = 'boot-stage';
  label.className = 'kit-title text-base uppercase';
  header.appendChild(label);

  const barFrame = document.createElement('div');
  barFrame.className = 'kit-bar w-full [--kh:28px]';
  const barFill = document.createElement('div');
  barFill.className = 'kit-bar-fill';
  barFill.style.width = '0%';
  barFrame.appendChild(barFill);

  const percent = document.createElement('div');
  percent.className = 'kit-title text-center font-heading text-xl tabular-nums';

  card.append(header, barFrame, percent);
  screen.append(brand, logo, card);
  container.appendChild(screen);

  let disposed = false;
  const dispose = (): void => {
    if (disposed) return;
    disposed = true;
    unsubscribe();
    screen.remove();
  };

  const render = (): void => {
    if (disposed) return;
    const pct = progress.overall();
    barFill.style.width = `${pct}%`;
    percent.textContent = `${Math.floor(pct)}%`;
    label.textContent = stageLabel(progress.current());
    if (progress.done) {
      // Fade, then remove. Under reduced motion the transition is globally forced to ~0ms
      // (app.css), so the timeout fires after an effectively instant fade.
      screen.style.opacity = '0';
      window.setTimeout(dispose, 320);
    }
  };

  const unsubscribe = progress.onChange(render);
  render();

  return { dispose };
}
